import { createSlice } from "@reduxjs/toolkit";
import { menu } from "../../../Components/Sidnavbar/menuData";
import { IMenuItem, ISideMenuPermission } from "../../../Components/Sidnavbar/ISidnavbar";

// 🔧 Helper: merges static menu with API-based permission data
const testArray = (arr: IMenuItem[], apiMenus: ISideMenuPermission[]): IMenuItem[] => {
  const seen = new Set<string>();

  const filtered = arr
    .map((item) => {
      const apiMatch = apiMenus.find((apiItem) => {
        if (item.to) {
          const normalize = (str: string) => str ? str.toLowerCase().replace(/^\/+/, "") : "";
          const itemTo = normalize(item.to);
          return (
            normalize(apiItem.screen_url) === itemTo ||
            normalize(apiItem.screen_path) === itemTo
          );
        }
        return (
          apiItem.screen_name?.toLowerCase() === item.to?.toLowerCase() ||
          apiItem.screen_name?.toLowerCase() === item.title?.toLowerCase()
        );
      });


      if (item.items && item.items.length > 0) {
        const filteredChildren = testArray(item.items, apiMenus);

        if (filteredChildren.length > 0 || apiMatch) {
          return {
            ...item,
            permissions: {
              add: apiMatch?.permissions?.add ?? false,
              view: apiMatch?.permissions?.view ?? !!apiMatch,
              edit: apiMatch?.permissions?.edit ?? false,
              delete: apiMatch?.permissions?.delete ?? false,
              show: apiMatch?.permissions?.is_visible ?? item.permissions?.show ?? false,
            },
            items: filteredChildren,
          };
        }
        return null;
      }


      if (apiMatch) {
        const uniqueKey = item.to?.toLowerCase() ?? `${item.title?.toLowerCase()}-${Math.random()}`;
        if (seen.has(uniqueKey)) return null;
        seen.add(uniqueKey);

        return {
          ...item,
          permissions: {
            add: apiMatch?.permissions?.add ?? false,
            view: apiMatch?.permissions?.view ?? true,
            edit: apiMatch?.permissions?.edit ?? false,
            delete: apiMatch?.permissions?.delete ?? false,
            show: apiMatch?.permissions?.is_visible ?? item.permissions?.show ?? false,
          },
        };
      }


      return null;
    })
    .filter(Boolean) as IMenuItem[];

  return filtered;
};


const flattenAPIMenus = (apiMenus: any[]): any[] => {
  let flat: any[] = [];
  apiMenus.forEach((apiItem) => {
    flat.push(apiItem);
    if (apiItem.children && apiItem.children.length > 0) {
      flat = flat.concat(flattenAPIMenus(apiItem.children));
    }
  });
  return flat;
};

const loopArray = (arr: { [key: string]: IMenuItem[] }, apiMenus: any[]) => {
  const flattenedApiMenus = flattenAPIMenus(apiMenus);
  const newArr: { [key: string]: IMenuItem[] } = {};
  Object.keys(arr).forEach((key) => {
    newArr[key] = testArray(arr[key], flattenedApiMenus);
  });
  return newArr;
};

// 🔧 Helper: Builds a nested menu structure purely from API data
const buildMenuHierarchy = (apiMenus: ISideMenuPermission[]): { [key: string]: IMenuItem[] } => {
  const transform = (apiItem: ISideMenuPermission): IMenuItem => {
    return {
      title: apiItem.screen_name,
      to: apiItem.screen_url || apiItem.screen_path,
      icon: apiItem.screen_icon,
      permissions: {
        add: apiItem.permissions?.add ?? false,
        view: apiItem.permissions?.view ?? true,
        edit: apiItem.permissions?.edit ?? false,
        delete: apiItem.permissions?.delete ?? false,
        show: apiItem.permissions?.is_visible ?? true,
      },
      items: apiItem.children && apiItem.children.length > 0
        ? apiItem.children.map(transform)
        : undefined,
    };
  };

  // Group root items under "Dashboard" key to satisfy the { [key: string]: IMenuItem[] } structure
  // This matches what TutorSideNavbar and other components expect.
  return {
    Dashboard: apiMenus.map(transform),
  };
};

export interface ISideMenu {
  data: { [key: string]: IMenuItem[] };
  isLoading?: boolean;
}


export const SideMenuPermissionSlice = createSlice({
  name: "SideMenuPermission",
  initialState: { data: { Dashboard: [] } } as ISideMenu,
  reducers: {
    listSideMenuPermission: (state) => {
      state.isLoading = true;
    },
    responseSideMenuPermission: (state, action) => {
      const apiData = action.payload;
      if (Array.isArray(apiData)) {
        const nestedMenu = buildMenuHierarchy(apiData);
        state.data = nestedMenu;
        console.log("✅ API Driven Menu Built:", nestedMenu);
      } else {
        state.data = {};
      }
      state.isLoading = false;
    },
    clearMenu: (state) => {
      state.data = {};
    },
  },
});

export const {
  listSideMenuPermission,
  responseSideMenuPermission,
  clearMenu,
} = SideMenuPermissionSlice.actions;

export default SideMenuPermissionSlice.reducer;
